"use client";

import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";

import NumberFlow from "@number-flow/react";
import { ScanSearch } from "lucide-react";
import { strings } from "@perceptron/constants";
import { Icon } from "@perceptron/ui/components/icon";
import {
  Play,
  Pause,
  Rewind,
  FastForward,
  SkipBack,
  SkipForward,
  Timer,
  Volume2,
  Volume1,
  VolumeX,
} from "lucide-react";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { useMediaState, useMediaRemote, MediaPlayerInstance } from "@vidstack/react";

import type { TimelinePlayerBridge } from "./bridge";
import { SEEK_AMOUNT, TEXT_COLOR } from "./constants";
import { useTimelineDataStore, useTimelineStore } from "./store/timeline-context";
import { useTrackingInspectorStore } from "./tracking/inspector/controller";
import { adjacentSegmentTime, segmentStartTimes } from "./utils/segment-navigation";
import { PlaybackRateControl } from "./PlaybackRateControl";

const sexagesimalDigits = { 1: { max: 6 } };

const trend = (oldValue: number, newValue: number) => {
  if (oldValue >= 50 && newValue < 10) return 1;
  if (oldValue < 10 && newValue >= 50) return -1;
  return Math.sign(newValue - oldValue);
};

const AnimatedTime = ({ time, className }: { time: number; className?: string }) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);

  return (
    <div className={cn("flex items-baseline tabular-nums", className)}>
      {hours > 0 && (
        <>
          <NumberFlow value={hours} />
          <span>:</span>
        </>
      )}
      <NumberFlow
        digits={hours > 0 ? sexagesimalDigits : undefined}
        format={{ minimumIntegerDigits: hours > 0 ? 2 : 1 }}
        trend={hours > 0 ? trend : undefined}
        value={minutes}
      />
      <span>:</span>
      <NumberFlow digits={sexagesimalDigits} format={{ minimumIntegerDigits: 2 }} trend={trend} value={seconds} />
    </div>
  );
};
const ControlButtons = React.memo(({ bridge, duration }: { bridge: TimelinePlayerBridge; duration: number }) => {
  const items = useTimelineDataStore((state) => state.items);
  const trackingTimeline = useTimelineStore((state) => state.trackingTimeline);

  const [paused, setPaused] = useState(() => bridge.isPaused());
  useEffect(() => bridge.subscribeToPaused(setPaused), [bridge]);

  const starts = useMemo(() => segmentStartTimes(items, trackingTimeline), [items, trackingTimeline]);

  const handlePlayPause = () => {
    if (paused) {
      bridge.play();
    } else {
      bridge.pause();
    }
  };

  const skipSegment = (direction: -1 | 1) => {
    if (bridge.skipSegment) {
      bridge.skipSegment(direction);
      return;
    }
    const target = adjacentSegmentTime(starts, bridge.getCurrentTime(), direction, duration);
    if (target !== undefined) bridge.seek(target);
  };

  const handleRewind = () => {
    const newTime = Math.max(0, bridge.getCurrentTime() - SEEK_AMOUNT);
    bridge.seek(newTime);
  };

  const handleFastForward = () => {
    const newTime = Math.min(duration, bridge.getCurrentTime() + SEEK_AMOUNT);
    bridge.seek(newTime);
  };

  return (
    <div className="flex items-center gap-8 md:gap-3" style={{ color: TEXT_COLOR }}>
      <button aria-label={formatString(strings.videoPlayer.rewind, SEEK_AMOUNT)} type="button" onClick={handleRewind}>
        <Rewind aria-hidden className="size-6 cursor-pointer hover:text-primary md:size-4" />
      </button>
      <button aria-label={strings.videoPlayer.previousSegment} type="button" onClick={() => skipSegment(-1)}>
        <SkipBack aria-hidden className="size-6 cursor-pointer hover:text-primary md:size-4" />
      </button>
      <button
        aria-label={paused ? strings.videoPlayer.play : strings.videoPlayer.pause}
        type="button"
        onClick={handlePlayPause}
      >
        <Play
          aria-hidden
          className={cn("size-10 cursor-pointer fill-white hover:text-primary md:size-4", {
            hidden: !paused,
          })}
          strokeWidth={0}
        />
        <Pause
          aria-hidden
          className={cn("size-10 cursor-pointer fill-white hover:text-primary md:size-4", {
            hidden: paused,
          })}
          strokeWidth={0}
        />
      </button>
      <button aria-label={strings.videoPlayer.nextSegment} type="button" onClick={() => skipSegment(1)}>
        <SkipForward aria-hidden className="size-6 cursor-pointer hover:text-primary md:size-4" />
      </button>
      <button
        aria-label={formatString(strings.videoPlayer.fastForward, SEEK_AMOUNT)}
        type="button"
        onClick={handleFastForward}
      >
        <FastForward aria-hidden className="size-6 cursor-pointer hover:text-primary md:size-4" />
      </button>
    </div>
  );
});

ControlButtons.displayName = "ControlButtons";

// Container that subscribes to playback time via the bridge. Time arrives at
// frame rate but the display shows whole seconds, so only a new second is state.
export const TimeDisplayContainer = ({ bridge, duration }: { bridge: TimelinePlayerBridge; duration: number }) => {
  const [currentSecond, setCurrentSecond] = useState(() => Math.floor(bridge.getCurrentTime()));
  useEffect(() => bridge.subscribeToTime((time) => setCurrentSecond(Math.floor(time))), [bridge]);

  return <TimeDisplay currentTime={currentSecond} duration={duration} />;
};

const TimeDisplay = React.memo(
  ({ currentTime, duration }: { currentTime: number; duration: number }) => {
    return (
      <div className="flex items-center gap-2" style={{ color: TEXT_COLOR }}>
        <Timer className="mb-0.5 size-4" />
        <div className="flex items-center gap-1 legal">
          <AnimatedTime time={currentTime} />
          <span>/</span>
          <AnimatedTime className="text-muted-foreground" time={duration} />
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // This memo is crucial: It ensures we only trigger the NumberFlow animation
    // when the integer second changes, preventing micro-jitter from 60fps updates.
    return (
      Math.floor(prevProps.currentTime) === Math.floor(nextProps.currentTime) &&
      Math.floor(prevProps.duration) === Math.floor(nextProps.duration)
    );
  },
);

TimeDisplay.displayName = "TimeDisplay";

const VolumeBar = ({ volume, onChange }: { volume: number; onChange: (volume: number) => void }) => (
  <div className="bg-muted/50 relative h-24 w-6 rounded-sm backdrop-blur-sm focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
    <div
      aria-hidden
      className="absolute bottom-0 left-0 right-0 rounded-sm bg-primary"
      style={{ height: `${volume}%` }}
    />
    {/* Native range semantics supply keyboard, pointer capture, and touch support.
        The existing filled bar is its visual presentation. */}
    <input
      aria-label={strings.videoPlayer.volume}
      aria-orientation="vertical"
      aria-valuetext={`${volume}%`}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      max={100}
      min={0}
      step={1}
      style={{ writingMode: "vertical-lr", direction: "rtl" }}
      type="range"
      value={volume}
      onChange={(event) => onChange(event.currentTarget.valueAsNumber)}
    />
  </div>
);

const VolumeControl = ({ playerRef }: { playerRef: React.RefObject<MediaPlayerInstance | null> }) => {
  const remote = useMediaRemote(playerRef);
  const currentVolume = useMediaState("volume", playerRef);
  const muted = useMediaState("muted", playerRef);
  const [showSlider, setShowSlider] = useState(false);
  const [volume, setVolume] = useState(() => Math.round(currentVolume * 100));
  const previousVolumeRef = useRef(volume || 100);
  const containerRef = useRef<HTMLDivElement>(null);
  const pointerInsideRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearHideTimeout, [clearHideTimeout]);

  useEffect(() => {
    const newVolume = Math.round(currentVolume * 100);
    setVolume(newVolume);
    if (newVolume > 0) previousVolumeRef.current = newVolume;
  }, [currentVolume]);

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (newVolume > 0) {
      previousVolumeRef.current = newVolume;
      // Vidstack restores 25% when unmuting from zero. Do that first so
      // the explicit slider value wins before media events update its state.
      if (muted) remote.unmute();
    }
    remote.changeVolume(newVolume / 100);
  };

  const show = () => {
    clearHideTimeout();
    setShowSlider(true);
  };

  const scheduleHide = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null;
      // Moving between the button and slider, or dragging outside the panel,
      // must not hide the control while it still owns keyboard focus.
      if (!pointerInsideRef.current && !containerRef.current?.contains(document.activeElement)) {
        setShowSlider(false);
      }
    }, 150);
  };

  const isSilent = muted || volume === 0;
  const handleClick = () => {
    if (isSilent) {
      remote.unmute();
      if (volume === 0) remote.changeVolume(previousVolumeRef.current / 100);
    } else {
      remote.mute();
    }
  };

  const displayedVolume = muted ? 0 : volume;
  const VolumeIcon = isSilent ? VolumeX : volume < 50 ? Volume1 : Volume2;

  return (
    <div
      className="relative flex items-center"
      ref={containerRef}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) scheduleHide();
      }}
      onFocusCapture={show}
      onPointerEnter={() => {
        pointerInsideRef.current = true;
        show();
      }}
      onPointerLeave={() => {
        pointerInsideRef.current = false;
        scheduleHide();
      }}
    >
      <button
        aria-label={isSilent ? strings.videoPlayer.unmute : strings.videoPlayer.mute}
        type="button"
        onClick={handleClick}
      >
        <VolumeIcon aria-hidden className="size-4 cursor-pointer hover:text-primary" style={{ color: TEXT_COLOR }} />
      </button>

      <div
        aria-hidden={!showSlider}
        className={cn(
          "absolute bottom-full left-1/2 mb-2 origin-bottom -translate-x-1/2 transition-all duration-200 ease-out",
          showSlider ? "scale-y-100 opacity-100" : "pointer-events-none scale-y-0 opacity-0",
        )}
        inert={!showSlider}
      >
        <VolumeBar volume={displayedVolume} onChange={handleVolumeChange} />
      </div>
    </div>
  );
};

/**
 * Opens the tracking inspector from the control tray, beside the other icon
 * controls. Renders only when the inspector is enabled, which is developer
 * mode — the same gate the panel itself uses.
 */
const TrackingInspectorControl = () => {
  const enabled = useTrackingInspectorStore((state) => state.enabled);
  const setOpen = useTrackingInspectorStore((state) => state.setOpen);
  if (!enabled) return null;
  return (
    <button
      aria-label={strings.trackingInspector.title}
      className="hidden rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-blue md:block"
      style={{ color: TEXT_COLOR }}
      title={strings.trackingInspector.title}
      type="button"
      onClick={() => setOpen(true)}
    >
      <ScanSearch aria-hidden className="size-4" />
    </button>
  );
};

const MediaControls = ({
  bridge,
  playerRef,
  className,
  onMenuOpenChange,
}: {
  bridge: TimelinePlayerBridge;
  playerRef: React.RefObject<MediaPlayerInstance | null>;
  className?: string;
  onMenuOpenChange?: (open: boolean) => void;
}) => {
  const duration = useMediaState("duration", playerRef);

  return (
    <div
      aria-label={strings.videoPlayer.controls}
      className={cn("flex flex-col items-center justify-center gap-6", "md:flex-row md:gap-0 md:py-2", className)}
      role="group"
    >
      {/* Volume sits on the left on desktop; hidden on mobile (system volume). */}
      <div className="hidden md:flex md:flex-1 md:justify-start">
        <VolumeControl playerRef={playerRef} />
      </div>
      {/* Transport buttons stay centered: two flex-1 wings on either side keep them midline. */}
      <div className="items-center justify-center md:flex">
        <ControlButtons bridge={bridge} duration={duration} />
      </div>

      <div className="flex items-center justify-end gap-2 md:flex-1">
        <PlaybackRateControl playerRef={playerRef} onOpenChange={onMenuOpenChange} />
        <TrackingInspectorControl />
        {bridge.showShortcuts && (
          <button
            aria-label={strings.videoShortcuts.title}
            className="hidden rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-blue md:block"
            style={{ color: TEXT_COLOR }}
            title={strings.videoShortcuts.title}
            type="button"
            onClick={bridge.showShortcuts}
          >
            <Icon aria-hidden className="size-4" name="help-circle" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MediaControls;
